/**
 * useLoggingConfig - React hook for logging configuration
 * 
 * Provides reactive access to logging configuration state and update methods.
 * Automatically re-renders components when configuration changes.
 * 
 * @example
 * function LoggingDashboard() {
 *   const { config, isEnabled, updateConfig, realm, setRealm } = useLoggingConfig();
 *   
 *   return (
 *     <div>
 *       <select value={realm || 'global'} onChange={e => setRealm(e.target.value)}>
 *         <option value="global">Global</option>
 *         <option value="tenant-abc">Tenant ABC</option>
 *       </select>
 *       
 *       <Switch
 *         checked={isEnabled('input.keyEvent')}
 *         onChange={checked => updateConfig('input.keyEvent', checked)}
 *       />
 *     </div>
 *   );
 * }
 */

import { useState, useEffect, useCallback } from 'react';
import {
  LoggingConfigService,
  LoggingConfig,
  LoggingConfigEntry,
  ComponentNode,
  ComponentRegistryResponse,
  UpdateConfigResponse,
  BulkUpdateResponse,
} from '../services/LoggingConfigService';

// Re-export types for convenience
export type {
  LoggingConfig,
  LoggingConfigEntry,
  ComponentNode,
  ComponentRegistryResponse,
  UpdateConfigResponse,
  BulkUpdateResponse,
};

export interface UseLoggingConfigResult {
  /** Current effective configuration */
  config: LoggingConfig;
  
  /** Check if a component is enabled */
  isEnabled: (component: string) => boolean;
  
  /** Update a single component's enabled state */
  updateConfig: (component: string, enabled: boolean, reason?: string) => Promise<UpdateConfigResponse>;
  
  /** Bulk update multiple components */
  bulkUpdate: (updates: Array<{ component: string; enabled: boolean }>, reason?: string) => Promise<BulkUpdateResponse>;
  
  /** Remove a realm-specific override */
  removeOverride: (component: string) => Promise<void>;
  
  /** Refresh configuration from backend */
  refreshConfig: () => Promise<void>;
  
  /** Current realm (null = global) */
  realm: string | null;
  
  /** Change the current realm */
  setRealm: (realm: string | null) => Promise<void>;
  
  /** Loading state */
  isLoading: boolean;
  
  /** Error state */
  error: Error | null;
}

export function useLoggingConfig(): UseLoggingConfigResult {
  const [config, setConfig] = useState<LoggingConfig>(LoggingConfigService.getConfig());
  const [realm, setRealmState] = useState<string | null>(LoggingConfigService.getRealm());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Subscribe to config changes
    const unsubscribe = LoggingConfigService.onConfigChange(() => {
      setConfig(LoggingConfigService.getConfig());
    });

    // Initial sync
    setConfig(LoggingConfigService.getConfig());
    setRealmState(LoggingConfigService.getRealm());

    return unsubscribe;
  }, []);

  const isEnabled = useCallback((component: string) => {
    return LoggingConfigService.isEnabled(component);
  }, []);

  const updateConfig = useCallback(async (
    component: string,
    enabled: boolean,
    reason?: string
  ): Promise<UpdateConfigResponse> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await LoggingConfigService.updateConfig(component, enabled, reason);
      setConfig(LoggingConfigService.getConfig());
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const bulkUpdate = useCallback(async (
    updates: Array<{ component: string; enabled: boolean }>,
    reason?: string
  ): Promise<BulkUpdateResponse> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await LoggingConfigService.bulkUpdate(updates, reason);
      setConfig(LoggingConfigService.getConfig());
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const removeOverride = useCallback(async (component: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await LoggingConfigService.removeOverride(component);
      setConfig(LoggingConfigService.getConfig());
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshConfig = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await LoggingConfigService.refreshConfig();
      setConfig(LoggingConfigService.getConfig());
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const setRealm = useCallback(async (newRealm: string | null): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await LoggingConfigService.setRealm(newRealm);
      setRealmState(newRealm);
      setConfig(LoggingConfigService.getConfig());
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    config,
    isEnabled,
    updateConfig,
    bulkUpdate,
    removeOverride,
    refreshConfig,
    realm,
    setRealm,
    isLoading,
    error,
  };
}

// ============================================================================
// ADDITIONAL HOOKS
// ============================================================================

/**
 * useComponentRegistry - Fetch and cache the component registry
 */
export function useComponentRegistry() {
  const [registry, setRegistry] = useState<ComponentRegistryResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    async function fetchRegistry() {
      try {
        const data = await LoggingConfigService.getComponentRegistry();
        if (mounted) {
          setRegistry(data);
          setIsLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Unknown error'));
          setIsLoading(false);
        }
      }
    }

    fetchRegistry();

    return () => {
      mounted = false;
    };
  }, []);

  return { registry, isLoading, error };
}

/**
 * useLoggingConfigInitialize - Initialize the logging config service
 * Call this hook once at the app root level
 */
export function useLoggingConfigInitialize(realm: string | null = null) {
  const [isInitialized, setIsInitialized] = useState<boolean>(LoggingConfigService.isInitialized());
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    async function initialize() {
      try {
        await LoggingConfigService.initialize(realm);
        if (mounted) {
          setIsInitialized(true);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Unknown error'));
        }
      }
    }

    if (!LoggingConfigService.isInitialized()) {
      initialize();
    }

    return () => {
      mounted = false;
    };
  }, [realm]);

  return { isInitialized, error };
}
